import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Calculator, ArrowLeft, DollarSign, Clock, Database, Loader2,
  Lightbulb, AlertTriangle, CheckCircle, Zap, Code, Play
} from 'lucide-react';
import { useSnowflakeCredentials } from '../components/SnowflakeConnect';
import SnowflakeConnect from '../components/SnowflakeConnect';
import axios from 'axios';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import BrandTitle from '../components/BrandTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QueryEstimator = () => {
  const { shouldShowData } = useDemoData();

  const [queryText, setQueryText] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [estimation, setEstimation] = useState(null);
  const { credentials, isConnected, updateCredentials } = useSnowflakeCredentials();

  const estimateQuery = async () => {
    if (!queryText.trim()) {
      toast.error('Please enter a SQL query');
      return;
    }

    if (!isConnected) {
      // Show demo estimation
      setEstimation({
        estimation: {
          estimated_credits: 12.5,
          estimated_cost_usd: 31.25,
          estimated_duration_seconds: 180,
          estimated_bytes_scanned: 5368709120,
          estimated_bytes_human: "5.00 GB",
          confidence: "medium"
        },
        optimization_suggestions: getOptimizationSuggestions(queryText),
        historical_reference: {
          similar_queries_analyzed: 23,
          average_cost_similar: 28.50
        }
      });
      toast.success('Demo estimation generated');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/snowflake/advanced/query-estimator`, {
        credentials,
        query_text: queryText
      });
      setEstimation(response.data);
      toast.success('Query cost estimated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to estimate query');
    } finally {
      setLoading(false);
    }
  };

  const getOptimizationSuggestions = (query) => {
    const suggestions = [];
    const upperQuery = query.toUpperCase();
    
    if (upperQuery.includes('SELECT *')) {
      suggestions.push({
        type: "column_selection",
        message: "Avoid SELECT * - specify only needed columns to reduce data scanned",
        potential_savings: "30-60%"
      });
    }
    
    if (!upperQuery.includes('WHERE')) {
      suggestions.push({
        type: "add_filter",
        message: "Add WHERE clause to filter data early",
        potential_savings: "50-90%"
      });
    }
    
    if (upperQuery.includes('JOIN')) {
      suggestions.push({
        type: "join_order",
        message: "Ensure smaller tables are on the right side of JOINs",
        potential_savings: "10-30%"
      });
    }
    
    if (upperQuery.includes('GROUP BY')) {
      suggestions.push({
        type: "aggregation",
        message: "Consider pre-aggregating data in a materialized view",
        potential_savings: "40-70%"
      });
    }
    
    if (suggestions.length === 0) {
      suggestions.push({
        type: "good",
        message: "Query looks well-optimized!",
        potential_savings: "0%"
      });
    }
    
    return suggestions;
  };

  const handleConnected = (creds) => {
    updateCredentials(creds);
    setShowConnect(false);
  };

  const sampleQueries = [
    {
      name: "Expensive JOIN",
      query: `SELECT * 
FROM fact_sales f
JOIN dim_products p ON f.product_id = p.product_id
JOIN dim_geography g ON f.geo_id = g.geo_id
GROUP BY p.category`
    },
    {
      name: "Full Table Scan",
      query: `SELECT COUNT(*) 
FROM events 
WHERE event_type = 'click'`
    },
    {
      name: "Optimized Query",
      query: `SELECT product_id, SUM(quantity) as total_qty
FROM fact_sales
WHERE sale_date >= '2026-01-01'
  AND region = 'US'
GROUP BY product_id
LIMIT 100`
    }
  ];

  if (!shouldShowData) {
    return <EmptyDataState title="Query Estimator" description="Connect your cloud account to see real data here." />;
  }


  return (
    <div className="space-y-6" data-testid="query-estimator">
      <SnowflakeConnect 
        isOpen={showConnect} 
        onClose={() => setShowConnect(false)}
        onConnected={handleConnected}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/snowflake">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
              <Calculator className="w-7 h-7 text-emerald-400" />
              <BrandTitle>Query Cost Estimator</BrandTitle>
            </h1>
            <p className="text-slate-700 text-sm">Know the cost before running your query</p>
          </div>
        </div>
        {!isConnected && (
          <Badge variant="outline" className="text-amber-600 border-amber-300">Demo Mode</Badge>
        )}
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Query Input */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-emerald-500" />
              Enter Your SQL Query
            </CardTitle>
            <CardDescription>Paste your query to estimate cost before running</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="SELECT * FROM your_table WHERE ..."
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              className="min-h-[200px] font-mono text-sm"
            />
            
            <div className="flex gap-2">
              <Button 
                onClick={estimateQuery} 
                disabled={loading || !queryText.trim()}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Estimating...
                  </>
                ) : (
                  <>
                    <Calculator className="w-4 h-4 mr-2" />
                    Estimate Cost
                  </>
                )}
              </Button>
            </div>

            {/* Sample Queries */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-400 mb-2">Try a sample query:</p>
              <div className="flex flex-wrap gap-2">
                {sampleQueries.map((sample) => (
                  <Button
                    key={sample.name}
                    variant="outline"
                    size="sm"
                    onClick={() => setQueryText(sample.query)}
                  >
                    {sample.name}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Estimation Results */}
        <div className="space-y-6">
          {estimation ? (
            <>
              {/* Cost Card */}
              <Card className="border-0 shadow-md bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-800">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-emerald-500" />
                      Estimated Cost
                    </span>
                    <Badge className={`${
                      estimation.estimation.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                      estimation.estimation.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {estimation.estimation.confidence} confidence
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-4">
                    <p className="text-5xl font-bold text-emerald-600 mb-2">
                      ${estimation.estimation.estimated_cost_usd.toFixed(2)}
                    </p>
                    <p className="text-slate-400">
                      ~{estimation.estimation.estimated_credits.toFixed(2)} credits
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="p-3 rounded-lg bg-white/50 dark:bg-slate-800/50 text-center">
                      <Clock className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                      <p className="text-sm text-slate-400">Duration</p>
                      <p className="font-semibold">{Math.round(estimation.estimation.estimated_duration_seconds / 60)} min</p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/50 dark:bg-slate-800/50 text-center">
                      <Database className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                      <p className="text-sm text-slate-400">Data Scanned</p>
                      <p className="font-semibold">{estimation.estimation.estimated_bytes_human}</p>
                    </div>
                  </div>

                  {estimation.historical_reference?.similar_queries_analyzed > 0 && (
                    <div className="mt-4 p-3 rounded-lg bg-slate-100 dark:bg-slate-700/50 text-center text-sm">
                      <p className="text-slate-400">
                        Based on <span className="font-semibold">{estimation.historical_reference.similar_queries_analyzed}</span> similar queries
                      </p>
                      <p className="text-slate-400 dark:text-slate-400">
                        Average cost: ${estimation.historical_reference.average_cost_similar.toFixed(2)}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Optimization Suggestions */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-500" />
                    Optimization Suggestions
                  </CardTitle>
                  <CardDescription>Ways to reduce the cost of this query</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {estimation.optimization_suggestions.map((suggestion, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${
                        suggestion.type === 'good' 
                          ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800'
                          : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
                      }`}>
                        <div className="flex items-start gap-2">
                          {suggestion.type === 'good' ? (
                            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <p className="text-sm text-slate-700 dark:text-slate-300">{suggestion.message}</p>
                            {suggestion.potential_savings !== "0%" && (
                              <p className="text-xs text-emerald-600 mt-1">
                                Potential savings: {suggestion.potential_savings}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-0 shadow-md">
              <CardContent className="p-12 text-center">
                <Calculator className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  Enter a Query to Estimate
                </h3>
                <p className="text-slate-400">
                  Paste your SQL query on the left to see estimated cost, duration, and optimization suggestions.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueryEstimator;
